import { useState } from "react";
import { texte } from "../../texte";
import { Overlay, ModalHeader } from "./Dialogs";
import {
  splitGoalChecks,
  joinGoalChecks,
  formatCondition,
  isValidCondition,
  type CheckKind,
} from "../../parse/goalchecks";

/** Spezial-Editor für goal.md (SPEC.md §4): trennt den freien Zieltext vom
 * '## Checks'-Block und bietet Buttons, die deterministische Bedingungen
 * (plan-condition-Syntax, SPEC.md §7) per Formular statt Handschrift anlegen.
 * `value`/`onChange` tragen weiterhin den vollen Dateitext — FileView.tsx
 * speichert unverändert per api.add(). */
export function GoalEditor({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const split = splitGoalChecks(value);
  const t = texte.goalEditor;
  const [addKind, setAddKind] = useState<CheckKind | null>(null);

  function setBody(body: string) {
    onChange(joinGoalChecks({ ...split, before: body }));
  }

  function setCheck(index: number, condition: string) {
    const checks = split.checks.slice();
    checks[index] = condition;
    onChange(joinGoalChecks({ ...split, checks }));
  }

  function removeCheck(index: number) {
    onChange(joinGoalChecks({ ...split, checks: split.checks.filter((_, i) => i !== index) }));
  }

  function addCheck(condition: string) {
    onChange(joinGoalChecks({ ...split, checks: [...split.checks, condition] }));
  }

  return (
    <div className="goal-editor">
      <span className="label">{t.bodyLabel}</span>
      <textarea
        className="file-edit-area goal-edit-area"
        value={split.before}
        onChange={(e) => setBody(e.target.value)}
        spellCheck={false}
        autoFocus
      />

      <div className="goal-checks">
        <div className="goal-checks-head">
          <span className="label">{t.checksHeading}</span>
          <div className="goal-checks-add">
            <button type="button" className="btn small" onClick={() => setAddKind("doc_exists")}>
              {t.addExists}
            </button>
            <button type="button" className="btn small" onClick={() => setAddKind("doc_contains")}>
              {t.addContains}
            </button>
          </div>
        </div>
        <p className="hint">{t.checksHint}</p>

        {split.checks.length === 0 ? (
          <p className="hint check-empty">{t.empty}</p>
        ) : (
          <div className="check-list">
            {split.checks.map((condition, i) => {
              const valid = isValidCondition(condition);
              return (
                <div className="check-row" key={i}>
                  <input
                    type="text"
                    className={valid ? "mono" : "mono invalid"}
                    value={condition}
                    onChange={(e) => setCheck(i, e.target.value)}
                    spellCheck={false}
                  />
                  {!valid && <span className="check-warn">{t.invalidHint}</span>}
                  <button
                    type="button"
                    className="btn ghost small"
                    onClick={() => removeCheck(i)}
                    aria-label={t.remove}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {addKind && (
        <AddCheckDialog kind={addKind} onClose={() => setAddKind(null)} onAdd={addCheck} />
      )}
    </div>
  );
}

function AddCheckDialog({
  kind,
  onClose,
  onAdd,
}: {
  kind: CheckKind;
  onClose: () => void;
  onAdd: (condition: string) => void;
}) {
  const t = texte.goalEditor.dialog;
  const [docId, setDocId] = useState("");
  const [pattern, setPattern] = useState("");
  const [negate, setNegate] = useState(false);

  const ready = docId.trim() !== "" && (kind === "doc_exists" || pattern.trim() !== "");
  const preview = ready ? formatCondition(kind, docId, pattern, negate) : "";

  function submit() {
    if (!ready) return;
    onAdd(formatCondition(kind, docId, pattern, negate));
    onClose();
  }

  return (
    <Overlay onClose={onClose}>
      <div className="modal">
        <ModalHeader title={kind === "doc_exists" ? t.titleExists : t.titleContains} onClose={onClose} />
        <div className="mb">
          <p className="hint" style={{ marginBottom: 14 }}>
            {kind === "doc_exists" ? t.explainExists : t.explainContains}
          </p>

          <div className="field" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="label">{t.docIdLabel}</span>
            <input
              type="text"
              value={docId}
              onChange={(e) => setDocId(e.target.value)}
              placeholder={t.docIdPlaceholder}
              autoFocus
            />
          </div>

          {kind === "doc_contains" && (
            <div className="field" style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
              <span className="label">{t.patternLabel}</span>
              <input
                type="text"
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                placeholder={t.patternPlaceholder}
              />
            </div>
          )}

          <label className="toggle-row" style={{ marginTop: 12 }}>
            <span className="txt">
              <b>{t.negateLabel}</b>
              <span className="hint">{t.negateExplain}</span>
            </span>
            <span className="switch">
              <input type="checkbox" checked={negate} onChange={(e) => setNegate(e.target.checked)} />
              <span className="tr" />
            </span>
          </label>

          {ready && (
            <p className="hint check-preview">
              {t.previewLabel}: <span className="mono">{preview}</span>
            </p>
          )}
        </div>
        <div className="mf">
          <button type="button" className="btn" onClick={onClose}>
            {t.cancel}
          </button>
          <button type="button" className="btn primary" disabled={!ready} onClick={submit}>
            {t.submit}
          </button>
        </div>
      </div>
    </Overlay>
  );
}
